/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Wed Sep 28 2022
*  File : LoginEvaluationExecutor.ts
*******************************************/

import { Logger } from "@ciaopeople/cp-shatsu/Logging";
import { EvaluatedActionType, isSubmitLogin, SubmitLogin } from "../../EvaluatedAction.js";
import { UserRole } from "../../../login/UserRole.js";
import { GoogleOneTapMomentType, isGoogleOneTapPromptNotification } from "../../../login/direct_providers/OneTapDelegate.js";
import { ClientSdkPublicInterface } from "../../../sdk/ClientSdkPublicInterface.js";
import { CTAExecutionResult, EvaluatedActionExecutor, NamedCtaEvaluator, EvaluationName, isDismissedCTA } from "../EvaluatedActionExecutor.js";
import { FollowSuggestionUtil } from "@api-commons/cta/FollowSuggestionUtil.js";

const DoEvaluateDataCompletionOnSuccessfulLogin = true;

export class LoginEvaluationExecutor implements EvaluatedActionExecutor {
    public isExpectedType = isSubmitLogin;

    constructor(
        private readonly sdk: ClientSdkPublicInterface,
        private logger: Logger
    ) {

    }

    execute(evaluator: NamedCtaEvaluator, res: SubmitLogin, data?: any): Promise<CTAExecutionResult> {
        if(res.data.oneTap) {
            this.logger.debug("Showing oneTap for", res);
            return this.sdk.doOneTapLogin(res.data.providerId[0], {}).then(lg => {
                let retPromise = Promise.resolve();
                this.logger.info("resOnEtAp", lg);
                if(DoEvaluateDataCompletionOnSuccessfulLogin) {
                    if(lg.session && lg.session.role == UserRole.AUTHENTICATED_USER) {
                        this.logger.info("Enqueueing data completion evaluation after successful onetap login", lg);

                        retPromise = evaluator.evaluateNamedCTAQueueUpdate(EvaluationName.PERSONAL_DATA_EVALUATION, true).then(_x => {
                            this.logger.debug("Coming back from onetap datacompletion");
                            return this.requireAfterLoginMessage(evaluator);
                            
                        });
                    }
                }
                else {
                    this.logger.warn("Inhibited data completion check by config on successful login");
                    retPromise = this.requireAfterLoginMessage(evaluator);
                }
                
                return retPromise.then(() => CTAExecutionResult.COMPLETED);

            }).catch(e => {
                if(isGoogleOneTapPromptNotification(e)) this.logger.info("Error executing oneTap", e);
                else this.logger.error("Unhanlded error on onetap login", e);
                return Promise.resolve(CTAExecutionResult.ERROR);
            });
        }
        else {
            return this.sdk.getGui().then(
                gui => {
                    const suggestionUtil = new FollowSuggestionUtil(this.sdk, this.logger);
                    return suggestionUtil.getPageSuggestedTopic().then(topic => {

                        

                        return this.sdk.preloadProviders(res.data.providerId, false).then(_e => gui.suggestLogin(
                            res.data.providerId,
                            topic
                        )).then(
                            allowed => {
                                if ( isDismissedCTA(allowed) ) {
                                    return CTAExecutionResult.DISMISSED;
                                }
                                else if (allowed) {

                                    const extra_params: any = {};
                                    if(topic) {
                                        extra_params.topic_url = topic.url;
                                    }
                                    extra_params.source_url = window.document.location.href;
                                    return this.sdk.login(allowed, extra_params, null).then(
                                        (lg) => {
                                            if (lg) {
                                                if(DoEvaluateDataCompletionOnSuccessfulLogin) {
                                                    if(lg.session && lg.session.role == UserRole.AUTHENTICATED_USER) {
                                                        this.logger.info("Enqueueing data completion evaluation after successful onetap login", lg);
                
                                                        return evaluator.evaluateNamedCTAQueueUpdate(EvaluationName.PERSONAL_DATA_EVALUATION, true).then(_x => {
                                                            this.logger.debug("Coming back from onetap datacompletion");
                
                                                            if (!res.data.fromBarrier) {
                                                                return this.requireAfterLoginMessage(evaluator);
                                                            }
                                                            else return Promise.resolve();
                                                        }).then(
                                                            () => CTAExecutionResult.COMPLETED
                                                        );
                                                    }
                                                    else {
                                                        this.logger.info("Unexpected login exitus while executing LoginEvaluationExecutor:", lg);
                                                        return Promise.resolve(CTAExecutionResult.ERROR);
                                                    }
                                                }
                                                else {
                                                    this.logger.warn("Inhibited data completion check by config on successful login");
                                                    let rPromise: Promise<void> = Promise.resolve();
                                                    if (!res.data.fromBarrier) {
                                                        rPromise = this.requireAfterLoginMessage(evaluator);
                                                    }
                                                    
                                                    return rPromise.then( _ => CTAExecutionResult.COMPLETED);
                                                }
                                            }
                                            else {
                                                return Promise.resolve(CTAExecutionResult.NOT_EXECUTED);
                                            }
                                        }
                                    );
                                }
                                else {
                                    return CTAExecutionResult.NOT_EXECUTED;
                                }
                            }
                        );
                    });
                });
        }
    }

    /**
     * richiede l'esecuzione dell'apertura del messaggio after login
     * @returns 
     */
    private requireAfterLoginMessage(evaluator: NamedCtaEvaluator): Promise<void> {
        this.logger.debug("Putting down after login message");
        return evaluator.enqueueActionAndAwait({
            type: EvaluatedActionType.AFTER_LOGIN_EVENTUALLY_PROPOSE_NOTIFICATIONS,
            data: null
        });
    }
}
